import {makeAutoObservable} from 'mobx';
import {parseApiError} from '../../../../lib/error-handler';

export interface ToastNotification {
    id: string;
    kind: 'success' | 'error' | 'info' | 'warning';
    title: string;
    subtitle: string;
    errorId?: string;
    action?: {
        label: string;
        onClick: () => void;
    };
    timeout?: number;
}

/**
 * Manages user-facing toast notifications for success, error, info, and warning messages.
 * Handles notification lifecycle including creation, dismissal, and automatic timeout.
 */
export class NotificationStore {
    notifications: ToastNotification[] = [];

    constructor() {
        makeAutoObservable(this);
    }

    showToastNotification(
        kind: ToastNotification['kind'],
        title: string,
        subtitle: string,
        options?: {
            errorId?: string;
            action?: {label: string; onClick: () => void};
            timeout?: number;
        }
    ): void {
        const id = crypto.randomUUID();
        const notification: ToastNotification = {
            id,
            kind,
            title,
            subtitle,
            ...options
        };
        this.notifications.push(notification);
    }

    hideToastNotification(id: string): void {
        const index = this.notifications.findIndex((n) => n.id === id);
        if (index !== -1) {
            this.notifications.splice(index, 1);
        }
    }

    isAbortError(error: any): boolean {
        return error?.name === 'AbortError' || error?.message === 'AbortError';
    }

    handleApiError(
        error: any,
        title: string,
        retryCallback?: () => void
    ): void {
        // Don't show notifications for abort errors (user-initiated cancellations)
        if (this.isAbortError(error)) {
            return;
        }

        const parsed = parseApiError(error);
        const options: any = {
            errorId: parsed.errorId
        };

        if (retryCallback) {
            options.action = {
                label: 'Retry',
                onClick: retryCallback
            };
        }

        this.showToastNotification('error', title, parsed.message, options);
    }

}
